import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import apiClient from "@/lib/api-client";
import { getApiErrorMessage } from "@/lib/errors";
import type { ClaimFolder } from "@/api/generated/client/models/ClaimFolder";
import type { User } from "@/api/generated/client/models/User";

export interface CreateFolderInput {
  name: string;
  color?: string | null;
}

export interface UpdateFolderInput {
  name?: string;
  color?: string | null;
}

const FOLDERS_QUERY_KEY = ["/api/claim-folders"] as const;

/**
 * Hook for managing claim folders (named, colored groupings of claims).
 * Provides the folder list (with claim counts) plus create/update/delete
 * mutations and the dialog state used by the folder management UI.
 */
export function useFolders() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<ClaimFolder | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<ClaimFolder | null>(null);

  // Current user (for admin gating)
  const { data: user } = useQuery({
    queryKey: ["/api/users/me"],
    queryFn: async (): Promise<User> => {
      return (await apiClient.users.getCurrentUser()) as User;
    },
  });

  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const currentUserId = user?.id;

  // Folder list (with claim counts)
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: FOLDERS_QUERY_KEY,
    queryFn: async () => {
      return await apiClient.claimFolders.getClaimFolders({ includeClaimCount: true });
    },
    enabled: !!user?.companyId,
  });

  // Full accessible folder list. For members this can include read-only folders
  // surfaced via shared claims (folders they can see but did not create).
  const folders: ClaimFolder[] = useMemo(
    () => (Array.isArray(data?.folders) ? data.folders : []),
    [data],
  );

  // Folders the current user can actually manage / assign claims into.
  // Admins and owners manage all company folders; members only their own.
  // The backend enforces the same rule (INVALID_CLAIM_FOLDER_ID / 403), so
  // exposing this here keeps the UI from offering actions guaranteed to fail.
  const manageableFolders: ClaimFolder[] = useMemo(
    () =>
      isAdmin
        ? folders
        : folders.filter((f) => f.createdBy === currentUserId),
    [folders, isAdmin, currentUserId],
  );

  const createFolderMutation = useMutation({
    mutationFn: async (input: CreateFolderInput) => {
      return await apiClient.claimFolders.createClaimFolder({
        createClaimFolderRequest: {
          name: input.name,
          color: input.color ?? null,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEY });
      toast({
        title: "Folder Created",
        description: "New folder has been created successfully.",
      });
      setCreateDialogOpen(false);
    },
    onError: async (err: unknown) => {
      toast({
        title: "Creation Failed",
        description: await getApiErrorMessage(err),
        variant: "destructive",
      });
    },
  });

  const updateFolderMutation = useMutation({
    mutationFn: async ({ id, data: input }: { id: number; data: UpdateFolderInput }) => {
      return await apiClient.claimFolders.updateClaimFolder({
        id,
        updateClaimFolderRequest: input,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEY });
      toast({
        title: "Folder Updated",
        description: "Folder details updated successfully.",
      });
      setEditDialogOpen(false);
      setEditingFolder(null);
    },
    onError: async (err: unknown) => {
      toast({
        title: "Update Failed",
        description: await getApiErrorMessage(err),
        variant: "destructive",
      });
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiClient.claimFolders.deleteClaimFolder({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEY });
      // Folder deletion unassigns claims, so refresh claim lists too.
      queryClient.invalidateQueries({ queryKey: ["claims"] });
      toast({
        title: "Folder Deleted",
        description: "Folder has been deleted. Claims in it were unfiled.",
      });
      setDeleteDialogOpen(false);
      setFolderToDelete(null);
    },
    onError: async (err: unknown) => {
      toast({
        title: "Delete Failed",
        description: await getApiErrorMessage(err),
        variant: "destructive",
      });
    },
  });

  return {
    folders,
    manageableFolders,
    isLoading,
    error,
    refetch,
    isAdmin,
    currentUserId,
    // Dialog state
    createDialogOpen,
    setCreateDialogOpen,
    editDialogOpen,
    setEditDialogOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    editingFolder,
    setEditingFolder,
    folderToDelete,
    setFolderToDelete,
    // Mutations
    createFolderMutation,
    updateFolderMutation,
    deleteFolderMutation,
  };
}
